import React, { useState, useRef, useEffect } from "react";
import { COUNTRY_CODES } from "../constants/countryCodes";
export { COUNTRY_CODES };

/**
 * Country-wise phone number lengths (min, max digits).
 * For countries not listed, defaults to (6, 15) — ITU standard.
 */
export const PHONE_LENGTHS = {
  "+91": { min: 10, max: 10, label: "10" },   // India
  "+1": { min: 10, max: 10, label: "10" },     // USA/Canada
  "+44": { min: 10, max: 10, label: "10" },    // UK
  "+92": { min: 10, max: 10, label: "10" },    // Pakistan
  "+880": { min: 10, max: 11, label: "10-11" }, // Bangladesh
  "+971": { min: 9, max: 9, label: "9" },      // UAE
  "+966": { min: 9, max: 9, label: "9" },      // Saudi Arabia
  "+81": { min: 10, max: 11, label: "10-11" }, // Japan
  "+86": { min: 11, max: 11, label: "11" },    // China
  "+55": { min: 10, max: 11, label: "10-11" }, // Brazil
  "+7": { min: 10, max: 10, label: "10" },     // Russia/Kazakhstan
  "+49": { min: 10, max: 11, label: "10-11" }, // Germany
  "+33": { min: 9, max: 9, label: "9" },       // France
  "+39": { min: 9, max: 10, label: "9-10" },   // Italy
  "+34": { min: 9, max: 9, label: "9" },       // Spain
  "+61": { min: 9, max: 9, label: "9" },       // Australia
  "+82": { min: 9, max: 10, label: "9-10" },   // South Korea
  "+62": { min: 9, max: 12, label: "9-12" },   // Indonesia
  "+63": { min: 10, max: 10, label: "10" },    // Philippines
  "+60": { min: 9, max: 10, label: "9-10" },   // Malaysia
  "+65": { min: 8, max: 8, label: "8" },       // Singapore
  "+94": { min: 9, max: 9, label: "9" },       // Sri Lanka
  "+977": { min: 10, max: 10, label: "10" },   // Nepal
  "+27": { min: 9, max: 9, label: "9" },       // South Africa
  "+234": { min: 10, max: 10, label: "10" },   // Nigeria
  "+254": { min: 9, max: 9, label: "9" },      // Kenya
  "+20": { min: 10, max: 10, label: "10" },    // Egypt
  "+212": { min: 9, max: 9, label: "9" },      // Morocco
  "+213": { min: 9, max: 9, label: "9" },      // Algeria
};

const COUNTRY_DIAL_BY_ISO = {
  in: "+91",
  us: "+1",
  ca: "+1",
  gb: "+44",
  ae: "+971",
  au: "+61",
  sg: "+65",
};

export function getPhoneLength(countryCode) {
  return PHONE_LENGTHS[countryCode] || { min: 6, max: 15, label: "6-15" };
}

function getDialCodeFromCountry(country) {
  if (!country) return "+91";
  return COUNTRY_DIAL_BY_ISO[String(country).toLowerCase()] || "+91";
}

function codeDigits(countryCode) {
  return String(countryCode || "").replace(/\D/g, "");
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

/**
 * PhoneInput — drop-in phone input component with:
 *  - Country code picker (same list as SignUp.js)
 *  - Numeric-only input with country-wise max-length validation
 *  - Error hint below input
 *
 * Props:
 *  - value:         current phone number string (digits only)
 *  - countryCode:   current country code string e.g. "+91"
 *  - onChange(phone, countryCode): called on any change
 *  - placeholder:   input placeholder
 *  - className:     extra classes for the wrapper div
 *  - inputClassName: extra classes for the actual <input>
 *  - disabled:      boolean
 *  - id:            id for accessibility
 */
const PhoneInput = ({
  value = "",
  countryCode,
  country,
  onChange,
  placeholder = "Enter phone number",
  className = "",
  containerClass = "",
  inputClassName = "",
  inputClass = "",
  buttonClass = "",
  codePickerClass = "",
  dropdownClass = "",
  inputStyle,
  dropdownStyle,
  showSelectedCountryName = false,
  disabled = false,
  id,
}) => {
  const legacyFullValueMode = Boolean(country) && !countryCode;
  const resolveInitialCode = () => {
    if (countryCode) return countryCode;
    const str = String(value || "").trim();
    if (str.startsWith("+")) {
      const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
      const found = sorted.find((c) => str.startsWith(c.code));
      if (found) return found.code;
    }
    return getDialCodeFromCountry(country);
  };
  const [localCode, setLocalCode] = useState(resolveInitialCode);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    if (countryCode) {
      setLocalCode(countryCode);
    } else {
      const str = String(value || "").trim();
      if (str.startsWith("+")) {
        const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
        const found = sorted.find((c) => str.startsWith(c.code));
        if (found) {
          setLocalCode(found.code);
          return;
        }
      }
      setLocalCode(getDialCodeFromCountry(country));
    }
  }, [countryCode, country, value]);

  useEffect(() => {
    const onOutside = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const { max, label } = getPhoneLength(localCode);
  const displayValue = (() => {
    let clean = String(value || "").trim();
    if (clean.startsWith("+")) {
      const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
      const found = sorted.find((c) => clean.startsWith(c.code));
      if (found) {
        clean = clean.slice(found.code.length).trim();
      }
    }
    const digits = digitsOnly(clean);
    return digits.slice(0, max);
  })();
  const filtered = COUNTRY_CODES.filter((c) =>
    !search || c.code.includes(search) || c.country.toLowerCase().includes(search.toLowerCase())
  );
  const selected = COUNTRY_CODES.find((c) => c.code === localCode);
  const emitChange = (phone, code) => {
    const normalizedPhone = digitsOnly(phone).slice(0, getPhoneLength(code).max);
    if (legacyFullValueMode) {
      onChange?.(`${codeDigits(code)}${normalizedPhone}`, code);
      return;
    }
    onChange?.(normalizedPhone, code);
  };

  const handleCodeSelect = (item) => {
    setLocalCode(item.code);
    setOpen(false);
    setSearch("");
    // Trim current phone to new country's max length
    const trimmed = displayValue.slice(0, getPhoneLength(item.code).max);
    emitChange(trimmed, item.code);
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw.length > max) {
      emitChange(raw.slice(0, max), localCode);
      return;
    }
    emitChange(raw, localCode);
  };

  const isValid =
    !displayValue ||
    (displayValue.length >= getPhoneLength(localCode).min && displayValue.length <= max);

  return (
    <div className={`flex items-start gap-2 ${className} ${containerClass}`}>
      {/* Country code picker */}
      <div className={`relative flex-shrink-0 ${showSelectedCountryName ? "w-[132px]" : "w-[84px]"} ${codePickerClass}`} ref={dropRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => { setOpen((o) => !o); setSearch(""); }}
          className={`w-full h-10 flex items-center justify-between gap-1.5 px-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${buttonClass}`}
        >
          <span
            className={`phone-input-selected-code text-current font-semibold text-xs ${
              showSelectedCountryName ? "" : "flex-1 text-center truncate"
            }`}
          >
            {selected?.code || localCode}
          </span>
          {showSelectedCountryName && (
            <span className="phone-input-selected-country text-[11px] text-gray-500 truncate flex-1 text-left">{selected?.country || ""}</span>
          )}
          <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && (
          <div
            className={`phone-input-dropdown absolute z-[9999] top-full mt-1 left-0 w-64 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden ${dropdownClass}`}
            style={dropdownStyle}
          >
            <div className="phone-input-dropdown-search p-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country name or code..."
                className="phone-input-search-input w-full px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <ul className="phone-input-dropdown-list max-h-48 overflow-y-auto">
              {filtered.map((item, i) => (
                <li
                  key={`${item.code}-${i}`}
                  onMouseDown={() => handleCodeSelect(item)}
                  className={`phone-input-dropdown-option px-3 py-2 flex items-center gap-2 text-xs cursor-pointer hover:bg-blue-50 ${localCode === item.code ? "bg-blue-50 font-semibold" : ""}`}
                >
                  <span className="font-semibold text-gray-600 w-12 flex-shrink-0">{item.code}</span>
                  <span className="text-gray-700 truncate">{item.country}</span>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-3 text-xs text-gray-400 text-center">No results</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Number input */}
      <div className="flex-1">
        <input
          id={id}
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={max}
          value={displayValue}
          onChange={handlePhoneChange}
          disabled={disabled}
          placeholder={`${placeholder} (${label} digits)`}
          style={inputStyle}
          className={`w-full h-10 px-4 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            !isValid && displayValue
              ? "border-red-300 focus:ring-red-500/20 focus:border-red-400"
              : "border-gray-200 focus:ring-blue-500/10 focus:border-blue-500"
          } ${inputClassName} ${inputClass}`}
        />
        {!isValid && displayValue && (
          <p className="mt-1 text-xs text-red-500">
            Phone must be {label} digit{label === "10" ? "s" : ""} for {selected?.country || localCode}
          </p>
        )}
      </div>
    </div>
  );
};

export default PhoneInput;
